import { format, formatDollars } from '@fruitful/fruitils';
import type { ImAccount } from '@fruitful/types';
import { router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { useState } from 'react';
import {
    BanknoteArrowUpIcon,
    BanknoteIcon,
    BodyMediumRegular,
    CircleDashedIcon,
    Clock10Icon,
    DownloadIcon,
    FullScreenSpinner,
    RowList,
    ScreenContainer,
    Sheet,
    TextRow,
    TrendingUpIcon,
    UploadIcon,
    YStack,
} from 'smoothie';

import { useTrackViewOnFocus } from '../../../events/hooks/useTrackViewOnFocus';
import { useGetApexAccountDetails } from '../../hooks/useGetApexAccountDetails';
import { useGetApexActivities } from '../../hooks/useGetApexActivities';
import {
    type ApexActivity,
    formatApexDate,
    type PendingApexAccountTransaction,
} from '../../types/activities';
import { buildDescription } from '../../types/transactions';
import { ApexAccountTransaction } from './ApexAccountTransaction';

const formatPendingDate = (p: PendingApexAccountTransaction): string => {
    const dateStr =
        p.kind === 'ict_deposit'
            ? (p.expectedSettlementDate ?? p.createdAt)
            : p.createdAt;
    return format(new Date(dateStr), 'M/d/yyyy');
};

const pendingTransactionTitle = (p: PendingApexAccountTransaction): string => {
    if (p.kind === 'ict_deposit') return 'Deposit';
    if (p.kind === 'ict_withdrawal_request') return 'Withdrawal requested';
    return p.direction === 'INBOUND' ? 'Deposit' : 'Withdrawal';
};

const pendingTransactionKey = (p: PendingApexAccountTransaction): string => {
    if (p.kind === 'ict_deposit') return p.idempotencyKey;
    if (p.kind === 'ict_withdrawal_request') return p.id;
    return p.clientTransferId;
};

const formatPendingAmount = (p: PendingApexAccountTransaction): string => {
    if (p.amountCents == null) return '';
    return formatDollars(p.amountCents / 100);
};

const transactionTypeToIcon = (type: string) => {
    switch (type) {
        case 'CASH_DIVIDEND':
            return BanknoteArrowUpIcon;
        case 'STOCK_DIVIDEND':
            return TrendingUpIcon;
        case 'DEPOSIT':
            return DownloadIcon;
        case 'WITHDRAWL':
            return UploadIcon;
        case 'INTEREST_PAYMENT':
            return BanknoteIcon;
        default:
            return CircleDashedIcon;
    }
};

const TransactionDetails = ({
    isOpen,
    transaction,
    apexAccountDetails,
    onClose,
}: {
    isOpen: boolean;
    transaction: ApexActivity | null;
    apexAccountDetails: ImAccount;
    onClose: () => void;
}) => {
    if (transaction == null) {
        return null;
    }

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={true}
            title="Transaction details"
        >
            <ApexAccountTransaction
                transaction={transaction}
                apexAccount={{
                    accountNumber: apexAccountDetails.accountNumber,
                    nickname: apexAccountDetails.nickname,
                }}
            />
        </Sheet>
    );
};

export const Transactions = ({
    transactions,
    pendingTransactions,
    apexAccountDetails,
}: {
    transactions: ApexActivity[];
    pendingTransactions: PendingApexAccountTransaction[];
    apexAccountDetails: ImAccount;
}) => {
    const [showTransaction, setShowTransaction] = useState<ApexActivity | null>(
        null,
    );

    const hasAny = transactions.length > 0 || pendingTransactions.length > 0;

    if (!hasAny) {
        return (
            <YStack>
                <BodyMediumRegular>No transactions</BodyMediumRegular>
            </YStack>
        );
    }

    return (
        <>
            {pendingTransactions.map((p) => (
                <TextRow
                    key={pendingTransactionKey(p)}
                    title={pendingTransactionTitle(p)}
                    subtitle={formatPendingDate(p)}
                    secondaryText={formatPendingAmount(p)}
                    secondarySubtext="Pending"
                    secondarySubtextProps={{ color: '$gray1000' }}
                    leftIcon={Clock10Icon}
                />
            ))}
            {transactions.map((t: ApexActivity) => {
                const formattedDate = formatApexDate(t.activityDate);

                // If this is an account transfer and there is an asset listed then there will
                // not be a value or price.  In that case we just show nothing in the right-hand
                // text area.
                const secondaryText =
                    t.subtypeCategory === 'ACCOUNT_TRANSFER' &&
                    t.assetSymbol != null
                        ? ''
                        : formatDollars(parseFloat(t.netAmount.value));

                const description = buildDescription(t, apexAccountDetails);

                return (
                    <TextRow
                        key={t.activityId}
                        title={description}
                        subtitle={formattedDate}
                        secondaryText={secondaryText}
                        leftIcon={transactionTypeToIcon(t.subtypeCategory)}
                        onPress={() => {
                            setShowTransaction(t);
                        }}
                    />
                );
            })}
            <TransactionDetails
                isOpen={showTransaction != null}
                apexAccountDetails={apexAccountDetails}
                transaction={showTransaction}
                onClose={() => {
                    setShowTransaction(null);
                }}
            />
        </>
    );
};

const PendingRows = ({
    pendingTransactions,
}: {
    pendingTransactions: PendingApexAccountTransaction[];
}) =>
    pendingTransactions.map((p) => (
        <TextRow
            key={pendingTransactionKey(p)}
            title={pendingTransactionTitle(p)}
            subtitle={formatPendingDate(p)}
            secondaryText={formatPendingAmount(p)}
            secondarySubtext="Pending"
            secondarySubtextProps={{ color: '$gray1000' }}
            leftIcon={Clock10Icon}
        />
    ));

const PostedRows = ({
    transactions,
    apexAccountDetails,
    onSelectTransaction,
}: {
    transactions: ApexActivity[];
    apexAccountDetails: ImAccount;
    onSelectTransaction: (t: ApexActivity) => void;
}) =>
    transactions.map((t) => {
        const formattedDate = formatApexDate(t.activityDate);
        const secondaryText =
            t.subtypeCategory === 'ACCOUNT_TRANSFER' && t.assetSymbol != null
                ? ''
                : formatDollars(parseFloat(t.netAmount.value));
        const description = buildDescription(t, apexAccountDetails);
        return (
            <TextRow
                key={t.activityId}
                title={description}
                subtitle={formattedDate}
                secondaryText={secondaryText}
                leftIcon={transactionTypeToIcon(t.subtypeCategory)}
                onPress={() => {
                    onSelectTransaction(t);
                }}
            />
        );
    });

export const ApexAccountTransactions = () => {
    useTrackViewOnFocus('Investment/Transactions');
    const { apexAccountId } = useLocalSearchParams<{
        apexAccountId: string;
    }>();

    const [showTransaction, setShowTransaction] = useState<ApexActivity | null>(
        null,
    );

    const { isLoading: isLoadingDetails, data: apexAccountDetails } =
        useGetApexAccountDetails(apexAccountId);

    const { isLoading: isLoadingTransactions, data: transactionsResponse } =
        useGetApexActivities(apexAccountId);

    if (isLoadingTransactions || isLoadingDetails) {
        return <FullScreenSpinner />;
    }

    if (apexAccountDetails == null) {
        router.navigate('/home/investment');
        return;
    }

    const transactions = transactionsResponse?.transactions ?? [];
    const pendingTransactions = transactionsResponse?.pendingTransactions ?? [];

    const sortedTransactions = [...transactions].sort((a, b) => {
        const { day: aDay, month: aMonth, year: aYear } = a.activityDate;
        const { day: bDay, month: bMonth, year: bYear } = b.activityDate;
        const aDate = new Date(aYear, aMonth - 1, aDay);
        const bDate = new Date(bYear, bMonth - 1, bDay);
        return bDate.getTime() - aDate.getTime();
    });

    const hasAny =
        sortedTransactions.length > 0 || pendingTransactions.length > 0;

    return (
        <ScreenContainer
            hasBackButton={true}
            title={apexAccountDetails.nickname}
            onFallbackRoute="/home/investment"
        >
            {!hasAny ? (
                <YStack gap={24}>
                    <BodyMediumRegular>No transactions</BodyMediumRegular>
                </YStack>
            ) : (
                <YStack gap={24}>
                    {pendingTransactions.length > 0 && (
                        <RowList rowHeaderProps={{ title: 'Pending' }}>
                            <PendingRows
                                pendingTransactions={pendingTransactions}
                            />
                        </RowList>
                    )}
                    {sortedTransactions.length > 0 && (
                        <RowList rowHeaderProps={{ title: 'Posted' }}>
                            <PostedRows
                                transactions={sortedTransactions}
                                apexAccountDetails={apexAccountDetails}
                                onSelectTransaction={setShowTransaction}
                            />
                        </RowList>
                    )}
                </YStack>
            )}
            <TransactionDetails
                isOpen={showTransaction != null}
                apexAccountDetails={apexAccountDetails}
                transaction={showTransaction}
                onClose={() => {
                    setShowTransaction(null);
                }}
            />
        </ScreenContainer>
    );
};
